import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Card, Col, Empty, Row, Space, Typography } from 'antd';
import moment from 'moment';
import { Field, Grid, GridCell, GridCellChangeRequest, GridRow } from '../../models';
import { apiClient, apiErrorMessage, getDataTypeName, parseHyperlink, hasEditReadOnlyPermission, hasEditInactivePermission } from '../../utils';
import { ChangeRequestActionButtons } from './change-request-action-buttons';
import 'moment/locale/tr';
import { ChangeRequestForm } from './cell-change-request-form';
import { CellValue } from '../../elements';
import { UserContext } from '../contexts';

export function CellEditPage(props: {
  grid?: Grid;
  gridId?: string;
  record?: GridRow;
  rowId?: string;
  field?: Field;
  allowRecordWithErrors?: boolean;
  onSaved: (row: GridRow) => void;
}) {
  const [changeRequestsHistoryLoading, setChangeRequestsHistoryLoading] = useState(false);
  const [cell, setCell] = useState<GridCell>();
  const [summaryFields, setSummaryFields] = useState<Field[]>();
  const [valueTasks, setValueTasks] = useState<GridCellChangeRequest[]>();
  const [changeRequestsHistory, setChangeRequestsHistory] = useState<GridCellChangeRequest[]>();
  const { user } = useContext(UserContext);
  const [readOnlyPermission, setReadOnlyPermission] = useState(false);
  const [passivePermission, setPassivePermission] = useState(false);
  const [predictions, setPredictions] = useState<{ value: string; confidence: string }[]>();
  const [showPredictionError, setShowPredictionError] = useState(false);

  const { Text } = Typography;

  const loadChangeRequestsHistory = useCallback(() => {
    if (!props.gridId || !props.rowId || !props.field) {
      return;
    }

    const queryParams = [`gridId=${props.gridId}`, `rowId=${props.rowId}`, `fieldId=${props.field?.id}`];
    const queryParamsStr = queryParams.join('&');

    setChangeRequestsHistoryLoading(true);
    apiClient
      .get(`records/change-requests?${queryParamsStr}`)
      .then((response: any) => {
        const requests = response.data as GridCellChangeRequest[];
        const pendingRequests = requests.filter(r => r.status === 'Pending');
        const historyRequests = requests.filter(r => r.status !== 'Pending');
        setChangeRequestsHistory(historyRequests);
        setValueTasks(pendingRequests);
      })
      .catch(() => {})
      .finally(() => setChangeRequestsHistoryLoading(false));
  }, [props.field, props.gridId, props.rowId]);

  const loadPredictions = useCallback(async () => {
    console.log('Loading predictions for gridId:', props.grid?.code, 'field code:', props.field?.code);

    if (props.grid?.code !== 'ZZ_PLM_KALEM_TABLOSU' || props.field?.code !== 'PO_CATEGORY_SEGMENT_COMBINATION') {
      console.log('Prediction not applicable for this grid/field.');
      return;
    }

    setShowPredictionError(false);

    apiClient
      .get(`suggestions/prediction?gridId=${props.gridId}&fieldId=${props.field?.id}&recordId=${props.record?.id}`)
      .then((response: any) => {
        const responseData = response.data as { value: string; confidence: string }[];
        console.log('Prediction response:', responseData);
        setPredictions(responseData);
        if (!responseData?.length) {
          console.log('No predictions returned from API.');
          setShowPredictionError(true);
        }
      })
      .catch(() => {
        console.log('Error occurred while fetching predictions.');
        setShowPredictionError(true);
      });
  }, [props.field?.code, props.field?.id, props.grid?.code, props.gridId, props.record?.id]);

  useEffect(() => {
    if (props.record) {
      const fields = props.grid?.fields?.filter(f => f.isIdentifier && !f.isRevision);
      const revisionField = props.grid?.fields?.find(f => f.isRevision);
      if (!!revisionField) {
        fields?.push(revisionField);
      }
      setSummaryFields(fields);
    }

    if (props.field) {
      setCell(props.record?.cells[props.field?.id]);
    }
  }, [props.field, props.grid, props.record]);

  useEffect(() => {
    loadChangeRequestsHistory();
  }, [loadChangeRequestsHistory]);

  useEffect(() => {
    loadPredictions();
  }, [loadPredictions]);

  useEffect(() => {
    setReadOnlyPermission(hasEditReadOnlyPermission(user, props.gridId));
    setPassivePermission(hasEditInactivePermission(user, props.gridId));
  }, [props.gridId, user]);

  const onSaved = (row: GridRow) => {
    props.onSaved(row);
    if (!props.field) {
      return;
    }
    const newCell = row.cells[props.field?.id];
    setCell(newCell);
    loadChangeRequestsHistory();
  };

  return (
    <>
      {(summaryFields?.length || 0) > 0 && (
        <Space split="," style={{ fontSize: '1.1rem', marginBottom: '1rem', paddingLeft: '0.5rem' }}>
          {summaryFields?.map(f => (
            <Space>
              <Text strong>{f.name}:</Text>
              <Text>{props.record?.cells?.[f.id]?.value}</Text>
            </Space>
          ))}
        </Space>
      )}
      <Row gutter={[32, 32]} style={{ marginBottom: '2rem' }}>
        <Col sm={24} md={12}>
          <PendingChanges
            field={props.field}
            valueTasks={valueTasks}
            canEvaluate={
              (cell?.vtCount || 0) > 0 &&
              (!props.record?.isReadOnly || readOnlyPermission) &&
              (!props.record?.isPassive || passivePermission)
            }
            predictions={predictions}
            onSaved={onSaved}
          />
        </Col>
        <Col sm={24} md={12}>
          {props.record?.isReadOnly && !readOnlyPermission && (
            <Alert
              type="warning"
              message="Bu kayıt salt okunabilir durumda olduğu için değişiklik yapılamaz."
              style={{ marginBottom: '1rem' }}
            />
          )}
          {props.record?.isPassive && !passivePermission && (
            <Alert type="warning" message="Bu kayıt pasif durumda olduğu için değişiklik yapılamaz." style={{ marginBottom: '1rem' }} />
          )}
          {(!props.record?.isReadOnly || readOnlyPermission) && (!props.record?.isPassive || passivePermission) && (
            <ChangeRequest
              gridId={props.gridId}
              rowId={props.rowId}
              field={props.field}
              value={cell?.valueRef || cell?.value}
              canDoVt={cell?.canDoVt}
              onSaved={onSaved}
              allowRecordWithErrors={props.allowRecordWithErrors}
              predictions={predictions}
              showPredictionError={showPredictionError}
            />
          )}
        </Col>
        <Col sm={24} md={12}>
          <CellValuesHistory field={props.field} changeRequests={changeRequestsHistory} loading={changeRequestsHistoryLoading} />
        </Col>
        <Col sm={24} md={12}>
          <CellInsights gridId={props.gridId} field={props.field} cell={cell} isObsolete={props.record?.isObsolete} />
        </Col>
      </Row>
    </>
  );
}

function PendingChanges(props: {
  field?: Field;
  valueTasks?: GridCellChangeRequest[];
  canEvaluate?: boolean;
  loading?: boolean;
  predictions?: { value: string; confidence: string }[];
  onSaved: (row: GridRow) => void;
}) {
  const { Text } = Typography;

  return (
    <Card title="Bekleyen onaylar" size="small" loading={props.loading} style={{ minHeight: '19rem' }}>
      <Space direction="vertical" size="large" style={{ width: '100%', height: '15rem', overflowY: 'auto' }}>
        {(props.valueTasks?.length || 0) <= 0 ? (
          <Empty style={{ marginTop: '1rem' }} description={<Text type="secondary">Bekleyen onay yok</Text>} />
        ) : (
          props.valueTasks?.map(vt => (
            <Row key={vt.id} align="middle">
              <Col span={10}>
                <Space direction="vertical" size={0}>
                  {vt.integrationExecution ? (
                    <Space>
                      <Text>Entegrasyon:</Text>
                      <Text style={{ textDecoration: vt.integrationExecution.integration.deletedAt ? 'line-through' : 'inherit' }}>
                        {vt.integrationExecution.integration.name}
                      </Text>
                    </Space>
                  ) : (
                    <Text>{vt.createdBy?.fullName}</Text>
                  )}
                  <Text type="secondary">{moment(vt.createdAt).format('DD.MM.yyyy HH:mm:ss')}</Text>
                </Space>
              </Col>
              <Col span={10}>
                <Text strong>
                  <CellValue
                    originalValue={vt.value}
                    cell={vt}
                    field={props.field}
                    missingReferenceStyle={true}
                    showNullText={true}
                    nullTextStyle={true}
                    showLink={true}
                    picklistImageInline={true}
                  />
                </Text>
              </Col>
              <Col span={4} style={{ textAlign: 'right' }}>
                <ChangeRequestActionButtons
                  valueTask={vt}
                  canEvaluate={props.canEvaluate}
                  predictions={props.predictions}
                  onSaved={props.onSaved}
                />
              </Col>
            </Row>
          ))
        )}
      </Space>
    </Card>
  );
}

function ChangeRequest(props: {
  gridId?: string;
  rowId?: string;
  field?: Field;
  value?: any;
  canDoVt?: boolean;
  allowRecordWithErrors?: boolean;
  predictions?: { value: string; confidence: string }[];
  showPredictionError?: boolean;
  onSaved: (row: GridRow) => void;
}) {
  return (
    <Card title="Yeni değer" size="small">
      <Space direction="vertical" size="large" style={{ width: '100%', padding: '0 3rem', height: '15rem', overflowY: 'auto' }}>
        <ChangeRequestForm
          gridId={props.gridId}
          rowId={props.rowId}
          field={props.field}
          onSaved={props.onSaved}
          value={props.value}
          canDoVt={props.canDoVt}
          allowRecordWithErrors={props.allowRecordWithErrors}
          predictions={props.predictions}
          showPredictionError={props.showPredictionError}
        />
      </Space>
    </Card>
  );
}

function CellValuesHistory(props: { field?: Field; changeRequests?: GridCellChangeRequest[]; loading?: boolean }) {
  const { Text } = Typography;

  return (
    <Card title="Geçmiş değişiklik talepleri" size="small" loading={props.loading} style={{ minHeight: '19rem' }}>
      <Space direction="vertical" size="large" style={{ width: '100%', height: '15rem', overflowY: 'auto' }}>
        {(props.changeRequests?.length || 0) <= 0 ? (
          <Empty style={{ marginTop: '1rem' }} description={<Text type="secondary">Geçmiş değişiklik talebi yok</Text>} />
        ) : (
          props.changeRequests?.map(cr => (
            <Row key={cr.id} align="middle">
              <Col span={10}>
                <Space direction="vertical" size={4}>
                  <Space split="-">
                    <Text>{cr.actionPerformedBy?.fullName || (cr.autoActionByAi ? 'Yapay zeka ile otomatik onay' : 'Otomatik onay')}</Text>
                    <Text type="secondary">{moment(cr.updatedAt).format('DD.MM.yyyy HH:mm:ss')}</Text>
                  </Space>
                  {cr.integrationExecution && (
                    <Space split="-" style={{ fontSize: '0.7rem' }}>
                      <Space>
                        <Text>Entegrasyon:</Text>
                        <Text style={{ textDecoration: cr.integrationExecution.integration.deletedAt ? 'line-through' : 'inherit' }}>
                          {cr.integrationExecution.integration.name}
                        </Text>
                      </Space>
                      <Text type="secondary">{moment(cr.integrationExecution.createdAt).format('DD.MM.yyyy HH:mm:ss')}</Text>
                    </Space>
                  )}
                  {cr.status === 'Rejected' && cr.actionMessage && (
                    <Alert
                      type="warning"
                      message={
                        <Space direction="vertical" size={0}>
                          <Text>{cr.actionMessage}</Text>
                          <Text type="secondary">
                            {cr.actionPerformedBy?.fullName || 'Otomatik onay'} - {moment(cr.updatedAt).format('DD.MM.yyyy HH:mm:ss')}
                          </Text>
                        </Space>
                      }
                      style={{ fontSize: '0.8rem' }}
                    />
                  )}
                </Space>
              </Col>
              <Col span={14} style={{ textAlign: 'right' }}>
                <Text strong type={cr.status === 'Approved' ? undefined : 'danger'}>
                  <CellValue
                    originalValue={cr.value}
                    cell={cr}
                    field={props.field}
                    showNullText={true}
                    nullTextStyle={true}
                    missingReferenceStyle={true}
                    showLink={true}
                    inheritColor={true}
                    picklistImageInline={true}
                  />
                </Text>
              </Col>
            </Row>
          ))
        )}
      </Space>
    </Card>
  );
}

function CellInsights(props: { gridId?: string; field?: Field; cell?: GridCell; isObsolete?: boolean }) {
  const { Text } = Typography;

  const [suggestions, setSuggestions] = useState<any>();

  useEffect(() => {
    if (props.cell && (props.field?.dataType === 'INTEGER' || props.field?.dataType === 'DOUBLE' || props.field?.dataType === 'DECIMAL')) {
      apiClient
        .get(`suggestions?gridId=${props.gridId}&fieldId=${props.field?.id}`)
        .then((response: any) => {
          setSuggestions(response.data);
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        });
    }
  }, [props.cell, props.field?.dataType, props.field?.id, props.gridId]);

  return (
    <Card size="small" title="Veri elemanı detayları">
      <Space direction="vertical" style={{ width: '100%', height: '15rem', overflowY: 'auto' }}>
        <>
          {(props.cell?.errors?.length || 0) > 0 && (
            <Row>
              <Col span={6}>Hatalar</Col>
              <Col span={18}>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {props.cell?.errors?.map((msg, i) => (
                    <li key={i}>
                      <Text type="danger">{msg}</Text>
                    </li>
                  ))}
                </ul>
              </Col>
            </Row>
          )}
        </>

        <Row style={{ alignItems: 'center' }}>
          <Col span={6}>Mevcut değer</Col>
          <Col span={18}>
            <Text type="secondary">
              <CellValue
                originalValue={props.cell?.value}
                cell={props.cell}
                field={props.field}
                isObsolete={props.isObsolete}
                missingReferenceStyle={true}
                showNullText={true}
                nullTextStyle={true}
                copyable={true}
                showLink={true}
              />
            </Text>
          </Col>
        </Row>

        <>
          {(props.field?.dataType === 'INTEGER' || props.field?.dataType === 'DOUBLE' || props.field?.dataType === 'DECIMAL') &&
            suggestions && (
              <>
                <Row>
                  <Col span={6}>En düşük değer</Col>
                  <Col>
                    <Text type="secondary">{suggestions.min}</Text>
                  </Col>
                </Row>
                <Row>
                  <Col span={6}>En yüksek değer</Col>
                  <Col>
                    <Text type="secondary">{suggestions.max}</Text>
                  </Col>
                </Row>
                <Row>
                  <Col span={6}>Ortalama değer</Col>
                  <Col>
                    <Text type="secondary">{suggestions.average}</Text>
                  </Col>
                </Row>
              </>
            )}
        </>

        <Row>
          <Col span={6}>Veri elemanı adı</Col>
          <Col span={18}>
            <Text type="secondary">{props.field?.name}</Text>
          </Col>
        </Row>
        <Row>
          <Col span={6}>Açıklama</Col>
          <Col span={18}>
            <Text type="secondary">{parseHyperlink(props.field?.description)}</Text>
          </Col>
        </Row>
        <Row>
          <Col span={6}>Veri tipi</Col>
          <Col span={18}>
            <Text type="secondary">{getDataTypeName(props.field?.dataType)}</Text>
          </Col>
        </Row>
        {props.field?.dataType === 'FOREIGNKEY' && !!props.field?.mainForeignKeyFieldId && (
          <Row>
            <Col span={6}>Bağlı referans</Col>
            <Col span={18}>
              <Text type="secondary">{props.field?.mainForeignKeyField.name}</Text>
            </Col>
          </Row>
        )}

        {props.field?.dataType !== 'DYNAMIC' && (
          <>
            <Row>
              <Col span={6}>Belirteç veri elemanı</Col>
              <Col span={18}>
                <Text type="secondary">{props.field?.isIdentifier ? 'Evet' : 'Hayır'}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={6}>Önem seviyesi</Col>
              <Col span={18}>
                <Text type="secondary">{props.field?.priority === 3 ? '!!!' : props.field?.priority === 2 ? '!!' : '!'}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={6}>Zorunlu</Col>
              <Col span={18}>
                <Text type="secondary">{props.field?.isRequired ? 'Evet' : 'Hayır'}</Text>
              </Col>
            </Row>
          </>
        )}

        {props.field?.minValue !== null && props.field?.minValue !== undefined && (
          <Row>
            <Col span={6}>En az</Col>
            <Col span={18}>
              <Text type="secondary">{props.field?.minValue}</Text>
            </Col>
          </Row>
        )}

        {props.field?.minValueField && (
          <Row>
            <Col span={6}>En az</Col>
            <Col span={18}>
              <Text type="secondary">{props.field?.minValueField?.name}</Text>
            </Col>
          </Row>
        )}

        {props.field?.maxValue !== null && props.field?.maxValue !== undefined && (
          <Row>
            <Col span={6}>En çok</Col>
            <Col span={18}>
              <Text type="secondary">{props.field?.maxValue}</Text>
            </Col>
          </Row>
        )}

        {props.field?.maxValueField && (
          <Row>
            <Col span={6}>En çok</Col>
            <Col span={18}>
              <Text type="secondary">{props.field?.maxValueField?.name}</Text>
            </Col>
          </Row>
        )}

        {props.field?.dataType === 'DYNAMIC' && (
          <Row>
            <Col span={6}>Şablon</Col>
            <Col span={18}>
              <Text type="secondary">{props.field?.dynamicTemplate}</Text>
            </Col>
          </Row>
        )}

        {props.field?.dynamicPostReplace && (
          <Row>
            <Col span={6}>Değer düzenleme</Col>
            <Col span={18}>
              {JSON.parse(props.field?.dynamicPostReplace)?.map((row: any) => {
                return (
                  <Col span={6}>
                    <Text type="secondary">{`${row.findValue} => ${row.newValue || ''}`}</Text>
                  </Col>
                );
              })}
            </Col>
          </Row>
        )}
      </Space>
    </Card>
  );
}
